<template>
  <!--
    The `value` attribute is sp-select's programmatic set channel: a clear or a URL restore lands,
    and Vue leaves the live selection alone between writes.
  -->
  <sp-select
    v-preserve-properties:items="items"
    class="availability-visit-type-select"
    :aria-label="prompt"
    navigation="accordion"
    selectable-branches
    :branch-toggle-label="branchToggleLabel"
    :value.attr="value"
    :prompt="prompt"
    :loading="loading"
    :disabled="selectDisabled"
    :data-testid="TestId.finder.availabilityVisitType"
    :item-test-id="TestId.finder.availabilityVisitTypeOption"
    @input="handleInput"
  ></sp-select>
</template>

<script setup>
import { vPreserveProperties } from "#ce/utils/property-bindings";
import { computed } from "vue";
import { firstDetail } from "#ce/utils/events";
import { toBoolean, emptyToUndefined } from "#ce/utils/props";
import { TestId } from "#/test-ids";
import { t } from "#ce/shared/filters/availability/i18n";

const emit = defineEmits(["select"]);

const props = defineProps({
  disabled: { type: Boolean, default: false },
  /** The selected row value: the comma-joined id list `visit_types` carries. */
  value: {
    type: String,
    default: undefined,
  },
  /**
   * The visit-type tree `useVisitTypeFilter` builds: `[{ title, value, children?, hasChildren? }]`,
   * its clearing row included. The clearing row is dropped here in favour of the prompt; the rest
   * renders as the tree it is, one row per category with its visit types nested under it.
   */
  nodes: {
    type: Array,
    default: () => [],
  },
  /** The row value that clears the selection. */
  anyValue: {
    type: String,
    required: true,
  },
  prompt: {
    type: String,
    required: true,
  },
  /** Whether the rows are being fetched. The select is disabled meanwhile, so a row a refresh may drop cannot be picked. */
  loading: {
    type: Boolean,
    default: false,
  },
});

const selectDisabled = computed(() => props.disabled || props.loading);

const items = computed(() => props.nodes.filter((node) => node.value !== props.anyValue));

const branchToggleLabel = computed(() => t("toggle_visit_types"));

function handleInput(event) {
  if (toBoolean(props.disabled)) {
    return;
  }
  emit("select", emptyToUndefined(firstDetail(event)));
}
</script>

<style lang="scss" scoped>
.availability-visit-type-select {
  display: block;
  width: 100%;
}
</style>
